import cx from 'classnames';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import GridLayout from 'react-grid-layout';
import { DragIcon } from '@Icons-outdated';
import { Tooltip } from '@ds';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
  BATCH_CHART_CONFIG,
  LAYOUT_MARGIN,
  LAYOUT_SETTING_MARGIN
} from '@experiment-management-shared/constants/chartConstants';
import { useObserveResizeNode } from '@shared/hooks/useObserveResizeNode';
import useBatchChartContainerGridHandlers from '@experiment-management-shared/hooks/useBatchChartContainerGridHandlers';
import InfiniteScroll from 'react-infinite-scroll-component';

import Chart from '@experiment-management-shared/components/Charts/Chart';
import {
  useBatchChartRenderQueue,
  useBatchChartContainerInfiniteScroll,
  useScrollPositionInBatchChartContainer
} from '@experiment-management-shared/components/Charts/hooks';
import { ChartPlaceholderLoading } from '@experiment-management-shared/components/Charts';
import useCachedPanels from '@experiment-management-shared/components/Charts/hooks/useCachedPanels';
import { calculatePanelPosition } from '@experiment-management-shared/components/Charts/hooks/utils';

const { COLUMNS, ROW_HEIGHT } = BATCH_CHART_CONFIG;

const BatchChartContainer = ({
  configs,
  entireRowCharts,
  isSearchMode,
  layout,
  onPanelChange,
  onPanelAndLayoutChange,
  parallelRender,
  visibilityDebounceDelay,
  width,
  panelSharedConfig
}) => {
  const gridRef = useRef(null);

  const {
    renderQueue,
    dataPanelsRows,
    addRenderQueue,
    removeRenderingQueue,
    onUpdateDataPanelRows,
    getDataRevision,
    onChartVisibilityChange,
    cleanRenderQueue
  } = useBatchChartRenderQueue({ parallelRender });

  const batchChartScrollPosition = useScrollPositionInBatchChartContainer({
    containerRef: gridRef
  });

  const {
    shownLayout,
    hasMorePanelsToRender,
    loadMorePanels
  } = useBatchChartContainerInfiniteScroll({
    entireRowCharts,
    isSearchMode,
    layout,
    configs
  });

  const cachedPanelActions = useCachedPanels();

  const handleDataSourceChange = useCallback(
    id => {
      addRenderQueue(id);
    },
    [addRenderQueue]
  );

  const handleFinishRender = useCallback(
    id => {
      removeRenderingQueue(id);
    },
    [removeRenderingQueue]
  );

  const ChartsProps = useMemo(() => {
    return configs.map(config => {
      const id = config.chartId;
      const dataRevision = getDataRevision(id);
      const isPendingRendering = renderQueue.includes(id);

      return {
        config,
        dataPanelsRows,
        dataRevision,
        isPendingRendering,
        onDataSourceChange: handleDataSourceChange,
        onFinishRender: handleFinishRender,
        onPanelChange,
        onUpdateDataPanelRows: onUpdateDataPanelRows,
        onVisibilityChange: onChartVisibilityChange,
        visibilityDebounceDelay,
        panelSharedConfig
      };
    });
  }, [
    configs,
    getDataRevision,
    renderQueue,
    dataPanelsRows,
    handleDataSourceChange,
    handleFinishRender,
    onPanelChange,
    onUpdateDataPanelRows,
    onChartVisibilityChange,
    visibilityDebounceDelay,
    panelSharedConfig
  ]);

  const { onDragStop, onResizeStop } = useBatchChartContainerGridHandlers({
    layout,
    onPanelAndLayoutChange
  });

  const [renderedCharts, chartIdsInView] = useMemo(() => {
    const chartsRenderedFromVirtualization = [];

    const resultLayout = shownLayout.map(chartLayout => {
      const chartPosition = calculatePanelPosition(
        chartLayout,
        null,
        ROW_HEIGHT,
        LAYOUT_MARGIN
      );

      const isPanelInView =
        chartPosition.bottomRight.y >= batchChartScrollPosition.top &&
        chartPosition.topLeft.y <= batchChartScrollPosition.bottom;

      const chartId = chartLayout.i;

      if (!isPanelInView && !cachedPanelActions.isPanelCached(chartId)) {
        return (
          <div className="chart-grid-item" key={chartId}>
            <ChartPlaceholderLoading />
          </div>
        );
      }

      if (isPanelInView) {
        chartsRenderedFromVirtualization.push(chartId);
      }

      const chartProps = ChartsProps.find(
        cp => cp?.config?.chartId === chartId
      );

      if (!chartProps) {
        return (
          <div className="chart-grid-item" key={chartId}>
            <ChartPlaceholderLoading />
          </div>
        );
      }

      if (isPanelInView) {
        cachedPanelActions.putCachedPanel(chartId);
      }

      return (
        <div className="chart-grid-item" key={chartLayout.i}>
          <Chart {...chartProps} />
          <Tooltip
            content="To drag and resize panels, first clear the search."
            disableListeners={!isSearchMode}
            wrapper={false}
          >
            <div
              className={cx('chart-drag-handle', {
                disabled: isSearchMode
              })}
            >
              <DragIcon />
            </div>
          </Tooltip>
        </div>
      );
    });

    cachedPanelActions.updateCachedPanels(
      chartsRenderedFromVirtualization?.length
    );

    return [resultLayout, cachedPanelActions.getCachedPanelIds()];
  }, [
    shownLayout,
    isSearchMode,

    ChartsProps,
    batchChartScrollPosition.top,
    batchChartScrollPosition.bottom,
    cachedPanelActions
  ]);

  const isInSearchModeConfigsChanged = isSearchMode && configs;

  useEffect(() => {
    cachedPanelActions.cleanCachedPanels([]);
  }, [isInSearchModeConfigsChanged]);

  useEffect(() => {
    cachedPanelActions.cleanCachedPanels(layout?.map(l => l.i));
  }, [layout]);

  useDeepCompareEffect(() => {
    cleanRenderQueue(chartIdsInView);
  }, [chartIdsInView]);

  return (
    <InfiniteScroll
      loader={null}
      endMessage={null}
      dataLength={shownLayout.length}
      next={loadMorePanels}
      hasMore={hasMorePanelsToRender}
      style={{ overflow: 'hidden' }}
    >
      <GridLayout
        compactType="vertical"
        preventCollision={false}
        className="edit-dashboard-layout-container"
        cols={COLUMNS}
        draggableHandle=".chart-drag-handle"
        resizeHandle={
          <div>
            <Tooltip
              content="To drag and resize panels, first clear the search."
              disableListeners={!isSearchMode}
              wrapper={false}
            >
              <div
                className={cx('react-resizable-handle', {
                  disabled: isSearchMode
                })}
              ></div>
            </Tooltip>
          </div>
        }
        isDraggable={!isSearchMode}
        isResizable={!isSearchMode}
        onDragStop={onDragStop}
        onResizeStop={onResizeStop}
        layout={shownLayout}
        rowHeight={ROW_HEIGHT}
        width={width}
        innerRef={gridRef}
        margin={LAYOUT_SETTING_MARGIN}
      >
        {renderedCharts}
      </GridLayout>
    </InfiniteScroll>
  );
};

BatchChartContainer.defaultProps = {
  configs: [],
  entireRowCharts: false,
  isSearchMode: false,
  layout: null,
  onLayoutChange: noop,
  onPanelChange: noop,
  parallelRender: 2,
  shouldSkipLayoutInitialization: false,
  visibilityDebounceDelay: 1000,
  width: 980
};

BatchChartContainer.propTypes = {
  configs: PropTypes.array,
  entireRowCharts: PropTypes.bool,
  isSearchMode: PropTypes.bool,
  layout: PropTypes.array,
  onLayoutChange: PropTypes.func,
  onPanelChange: PropTypes.func,
  onPanelAndLayoutChange: PropTypes.func,
  parallelRender: PropTypes.number,
  visibilityDebounceDelay: PropTypes.number,
  width: PropTypes.number,
  panelSharedConfig: PropTypes.object
};

const WidthContainer = props => {
  const [containerWidth, setContainerWidth] = useState(null);

  const resizeRef = useObserveResizeNode(node =>
    setContainerWidth(node.clientWidth)
  );

  if (!containerWidth) {
    return <div ref={resizeRef} style={{ height: '400px', width: '100%' }} />;
  }

  return (
    <div ref={resizeRef} style={{ width: '100%' }}>
      <BatchChartContainer {...props} width={containerWidth} />
    </div>
  );
};

export default WidthContainer;
